/**
 * useFocusTrap — traps keyboard focus inside a container element.
 *
 * When active:
 *   - Tab cycles forward through all focusable elements in the container.
 *   - Shift+Tab cycles backward.
 *   - Focus never escapes to the host page.
 *
 * On mount, moves focus to the first focusable element in the container.
 * On unmount, returns focus to `returnFocusTo` (typically the trigger button).
 *
 * No external libs — implementation is ~30 lines.
 */

import { useEffect } from 'preact/hooks';
import type { RefObject } from 'preact';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
  );
}

interface UseFocusTrapOptions {
  /** Ref to the container element that receives the trap. */
  containerRef: RefObject<HTMLElement>;
  /** Whether the trap is currently active. */
  active: boolean;
  /** Element to return focus to when the trap is deactivated (unmounted). */
  returnFocusTo?: HTMLElement | null;
  /**
   * When true, skip the initial focus grab on mount but keep the Tab
   * cycle active. Used by the auto-open flow: the widget expands on
   * page load but the visitor's keyboard focus stays wherever it was
   * on the host page (accessibility). If the visitor later tabs into
   * the panel, the trap still wraps them correctly.
   */
  skipInitialFocus?: boolean;
}

export function useFocusTrap({
  containerRef,
  active,
  returnFocusTo,
  skipInitialFocus = false,
}: UseFocusTrapOptions): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    // Move focus into the panel immediately — unless the caller asked to
    // preserve the host page's focus (auto-open flow).
    if (!skipInitialFocus) {
      const focusable = getFocusableElements(container);
      if (focusable.length > 0) {
        focusable[0].focus();
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const container = containerRef.current;
      if (!container) return;

      const focusable = getFocusableElements(container);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        // Shift+Tab: if at first element, wrap to last
        if (active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab: if at last element, wrap to first
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Return focus to the trigger element
      if (returnFocusTo && typeof returnFocusTo.focus === 'function') {
        returnFocusTo.focus();
      }
    };
  }, [active, containerRef, returnFocusTo, skipInitialFocus]);
}
